import React, { ComponentType, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { CommmuneRoute, EnhancedLocationDescriptor, SocialProfileRoot, V3Routes } from '../../routes';
import { Link } from 'react-router-dom';
import { routingStore } from '../../../common/stores/routing';
import { CoachSkillCertificationState } from '../../states/CoachSkillCertificationState';
import { TUTORIAL_COURSE_ID } from '../../types/courses';
import { inject } from 'mobx-react';
import { IconHome } from './IconHome';
import { IconCourse } from './IconCourse';
import {
  OpenInNewOutlined,
  GroupOutlined,
  BusinessCenterOutlined,
  QuestionAnswerOutlined,
  StarBorderOutlined,
  ComputerOutlined,
  CreateOutlined,
  AccountBoxOutlined,
  EventOutlined,
  NotificationsOutlined,
} from '@material-ui/icons';
import { HeaderLogo } from '../../components/atoms/Logo';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import { NotificationUnreadBadge } from './NotificationIcon';
import './desktopSideNavigation.module.scss';

type CommonNavItemProps = {
  id?: string;
  testId?: string;
  activeWhen?: string;
  icon: ReactNode;
  external?: boolean;
  label: string;
  subLabel?: string;
};

type NavItemProps =
  | (CommonNavItemProps & {
      to: EnhancedLocationDescriptor;
    })
  | (CommonNavItemProps & {
      href: string;
      target?: string;
      rel?: string;
    });

const NavItem = observer((props: NavItemProps) => {
  const { id, testId, activeWhen, icon, external, label, subLabel } = props;
  const active = activeWhen && routingStore.location.pathname.match(activeWhen);
  const isShownNotificationUnreadBadge = id === 'side-nav-notification';

  if ('href' in props && props.href) {
    const { href, target, rel } = props;

    return (
      <a
        id={id}
        data-testid={testId}
        styleName={classNames('nav-item', { active: active })}
        href={href}
        target={target}
        rel={rel}
      >
        <div styleName="nav-item-inner">
          {icon && (
            <span styleName="icon">
              {icon}
              {isShownNotificationUnreadBadge && <NotificationUnreadBadge />}
            </span>
          )}
          <div styleName="label-wrap">
            <span styleName="label">{label}</span>
            {subLabel && <span styleName="sub-label">{subLabel}</span>}
          </div>
          {external && (
            <span styleName="external-link-icon">
              <OpenInNewOutlined />
            </span>
          )}
        </div>
      </a>
    );
  }
  if ('to' in props && props.to) {
    const { to } = props;

    return (
      <Link styleName={classNames('nav-item', { active: active })} to={to} id={id}>
        <div styleName="nav-item-inner" data-testid={testId}>
          {icon && (
            <span styleName="icon">
              {icon}
              {isShownNotificationUnreadBadge && <NotificationUnreadBadge />}
            </span>
          )}
          <div styleName="label-wrap">
            <span styleName="label">{label}</span>
            {subLabel && <span styleName="sub-label">{subLabel}</span>}
          </div>
          {external && (
            <span styleName="external-link-icon">
              <OpenInNewOutlined />
            </span>
          )}
        </div>
      </Link>
    );
  }
  return null;
});

type CommonSubNavItemProps = {
  id?: string;
  testId?: string;
  activeWhen?: string;
  children: React.ReactNode;
  target?: string;
  rel?: string;
  onClick?: () => void;
};

type SubNavItemProps =
  | (CommonSubNavItemProps & {
      to: EnhancedLocationDescriptor;
    })
  | (CommonSubNavItemProps & {
      href: string;
    });

const SubNavItem = (props: SubNavItemProps) => {
  const { id, testId, activeWhen, children, target, rel, onClick } = props;
  const active = activeWhen && routingStore.location.pathname.match(activeWhen);
  if ('href' in props && props.href) {
    const { href } = props;
    return (
      <a
        id={id}
        data-testid={testId}
        styleName={classNames('sub-nav-item', { active: active })}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  if ('to' in props && props.to) {
    const { to } = props;
    return (
      <Link
        id={id}
        data-testid={testId}
        styleName={classNames('sub-nav-item', { active: active })}
        to={to}
        target={target}
        rel={rel}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }
  return null;
};

type InternalProps = {
  coachSkillState: CoachSkillCertificationState;
};

type SubNavigationComponentProps = {
  onClick?: () => void;
};

export const SubNavigationComponent = ({ onClick }: SubNavigationComponentProps) => {
  return (
    <div styleName="nav-sub-wrap">
      <SubNavItem
        testId="side-sub-nav-faq"
        to={V3Routes.faq()}
        activeWhen="/faq/?"
        onClick={onClick}
        target="_blank"
        rel="noopenner"
      >
        よくある質問
      </SubNavItem>
      {GlobalStatesV3.currentUser.hasEffectiveMembershipToken() && (
        <>
          <SubNavItem
            testId="side-sub-nav-satisfaction-survey"
            href={V3Routes.survey('overall_satisfaction').stringify()}
            onClick={onClick}
            target="_blank"
            rel="noopenner"
          >
            満足度アンケート
          </SubNavItem>
          <SubNavItem
            testId="side-sub-nav-creators-survey"
            href={V3Routes.survey('creators_survey').stringify()}
            onClick={onClick}
            target="_blank"
            rel="noopenner"
          >
            キャリアアンケート
          </SubNavItem>
          <SubNavItem testId="side-sub-nav-shelikes-101" to={V3Routes.course(TUTORIAL_COURSE_ID)} onClick={onClick}>
            SHElikesの使い方
          </SubNavItem>
        </>
      )}
    </div>
  );
};

const DesktopSideNavigationComponent = inject('coachSkillState')(
  observer(({ coachSkillState }: InternalProps) => {
    return (
      <div styleName="nav-wrapper" data-testid="desktop-side-navigation">
        <div styleName="nav-main-wrap">
          <div styleName="logo">
            <HeaderLogo onClick={() => GlobalStatesV3.routing.push('/')} />
          </div>
          <NavItem
            testId="side-nav-home"
            label=" ホーム"
            to={V3Routes.home()}
            activeWhen="^/?$|^/home/?$|^/home/reservations?$"
            icon={<IconHome styleName="icon-home" />}
          />
          <NavItem
            id="side-nav-notification"
            testId="side-nav-notification"
            label="お知らせ"
            to={V3Routes.NotificationDeliveryListPage()}
            activeWhen="/notifications/?"
            icon={<NotificationsOutlined />}
          />
          <NavItem
            testId="side-nav-courses"
            label="コース"
            to={V3Routes.courses()}
            activeWhen="/courses/?"
            icon={<IconCourse styleName="icon-course" />}
          />
          <NavItem
            testId="side-nav-coaching"
            label="コーチング"
            to={V3Routes.coaching()}
            activeWhen="/coaching/?"
            icon={<GroupOutlined />}
          />
          {GlobalStatesV3.currentUser.hasEffectiveMembershipToken() && (
            <NavItem
              testId="side-nav-commmune"
              label="コミュニティ"
              subLabel="（SHEstation）"
              href={V3Routes.home(CommmuneRoute()).stringify()}
              target="_blank"
              rel="noopenner"
              icon={<QuestionAnswerOutlined />}
              external
            />
          )}
          <NavItem
            testId="side-nav-events"
            label="イベント"
            to={V3Routes.events()}
            activeWhen="/events/?"
            icon={<StarBorderOutlined />}
          />
          <NavItem
            testId="side-nav-mokumoku"
            label="もくもく会"
            to={V3Routes.mokumoku()}
            activeWhen="/mokumoku/?"
            icon={<ComputerOutlined />}
          />
          {GlobalStatesV3.currentUser.hasEffectiveMembershipToken() && (
            <>
              <NavItem
                testId="side-nav-course-work-submissions"
                label="課題"
                to={V3Routes.userCourseWorkSubmissions()}
                activeWhen="^/course_work_submissions?|^/lesson_homework?|^/exam_?"
                icon={<CreateOutlined />}
              />
              <NavItem
                testId="side-nav-she-creators"
                label="お仕事案件"
                to={V3Routes.creators()}
                activeWhen="/she_creators/?"
                icon={<BusinessCenterOutlined />}
              />
              <NavItem
                testId="side-nav-social-profile"
                label="SHEプロフ"
                href={V3Routes.home(SocialProfileRoot(), 'shelikes-home').stringify()}
                target="_blank"
                rel="noopenner"
                icon={<AccountBoxOutlined />}
                external
              />
            </>
          )}
          {coachSkillState.hasAnySkill && (
            <>
              <NavItem
                testId="side-nav-timesheet"
                label="シフト"
                to={V3Routes.timesheetHome()}
                activeWhen="/timesheet/?"
                icon={<EventOutlined />}
              />
              <NavItem
                testId="side-nav-reviewer"
                label="課題添削"
                to={V3Routes.courseWorkSubmissions()}
                activeWhen="/reviewer/?"
                icon={<CreateOutlined />}
              />
            </>
          )}
        </div>
        <SubNavigationComponent />
      </div>
    );
  })
);

export const DesktopSideNavigation = DesktopSideNavigationComponent as ComponentType<InternalProps>;
